import * as React from 'react';
import { createContext, useContext } from 'react';

import {
  ProfileOccupationChoices_DemandLevel as DemandLevel,
  ProfileOccupationChoices_FeatureTag as FeatureTag,
} from '__generated__/graphql-types';
import type {
  ProfileOccupationChoices_OccupationChoice as LearnerProfileOccupationChoice,
  LearnerProfile_OccupationPreference as LearnerProfileOccupationPreference,
} from '__generated__/graphql-types-do-not-use';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import { useProfileOccupationChoices } from 'bundles/employability-common/queries/hooks/useProfileOccupationChoices';

export type OccupationIdToRoleMap = Map<string, LearnerProfileOccupationChoice>;

type Props = {
  children: React.ReactNode;
};

type SelectOption = {
  label: string;
  value: string;
};

export type OccupationRolesContextType = {
  idToRolesMap: OccupationIdToRoleMap;
  isRolesMapLoading: boolean;
  activelyHiringOccupationOptions: Array<SelectOption>;
  nonActivelyHiringOccupationOptions: Array<SelectOption>;
  talentPipelineIds: Set<string>;
  getSelectedTalentPipelineRoleIds: (preferredRoles?: LearnerProfileOccupationPreference[]) => string[];
};

export const OccupationRolesContext = createContext<OccupationRolesContextType>({
  idToRolesMap: new Map<string, LearnerProfileOccupationChoice>(),
  isRolesMapLoading: true,
  activelyHiringOccupationOptions: [],
  nonActivelyHiringOccupationOptions: [],
  talentPipelineIds: new Set<string>(),
  getSelectedTalentPipelineRoleIds: () => [],
});

export const OccupationRolesProvider = ({ children }: Props) => {
  const { occupations, isProfileOccupationChoicesLoading } = useProfileOccupationChoices({
    occupationChoiceFilter: {
      occupationTags: [
        { demandLevel: DemandLevel.Low, featureTag: FeatureTag.BaselineV2 },
        { demandLevel: DemandLevel.High, featureTag: FeatureTag.BaselineV2 },
        { demandLevel: DemandLevel.Low, featureTag: FeatureTag.PlpV1 },
        { demandLevel: DemandLevel.High, featureTag: FeatureTag.PlpV1 },
        { demandLevel: DemandLevel.High, featureTag: FeatureTag.ActivelyHiring },
      ],
    },
  });
  const { profileState = {} } = useContext(ProfileContext);
  const { inferredUserLocationCountryCode } = profileState;

  const idToRolesMap = new Map<string, LearnerProfileOccupationChoice>();
  const activelyHiringOccupationOptions: SelectOption[] = [];
  const nonActivelyHiringOccupationOptions: SelectOption[] = [];
  const talentPipelineIds: Set<string> = new Set();

  const handleRoleMapping = (role: LearnerProfileOccupationChoice) => {
    idToRolesMap.set(role.id, role);

    const PREFERRED_ROLE_FEATURE_TAG = 'ACTIVELY_HIRING';
    const PREFERRED_ROLE_DEMAND_LEVEL = 'HIGH';
    const roleFeatureTags = role.occupationTags.map((tag) => tag.featureTag);
    const roleDemandLevels = role.occupationTags.map((tag) => tag.demandLevel);

    // While iMocha is still running, we still want to have the software development role for India in the global
    // rollout. Ticket for iMocha skills assessments cleanup - https://coursera.atlassian.net/browse/LPD-804
    const isIndiaSoftwareDevelopmentRole =
      inferredUserLocationCountryCode === 'IN' && role.id === 'software-developer-engineer';

    if (
      isIndiaSoftwareDevelopmentRole ||
      (roleFeatureTags.includes(PREFERRED_ROLE_FEATURE_TAG) && roleDemandLevels.includes(PREFERRED_ROLE_DEMAND_LEVEL))
    ) {
      activelyHiringOccupationOptions.push({ label: role.occupationName, value: role.id });
      talentPipelineIds.add(role.id);
    } else {
      nonActivelyHiringOccupationOptions.push({ label: role.occupationName, value: role.id });
    }
  };

  const getSelectedTalentPipelineRoleIds = (preferredRoles: LearnerProfileOccupationPreference[] | undefined) => {
    // A talent piepline will always have an id, and won't be user generated
    return (preferredRoles ?? [])?.reduce(
      (acc, role) =>
        'id' in role.occupation && talentPipelineIds.has(role.occupation.id) ? [...acc, role.occupation.id] : acc,
      [] as string[]
    );
  };

  occupations?.forEach((role) => handleRoleMapping(role));

  return (
    <OccupationRolesContext.Provider
      value={{
        idToRolesMap,
        isRolesMapLoading: isProfileOccupationChoicesLoading,
        activelyHiringOccupationOptions,
        nonActivelyHiringOccupationOptions,
        talentPipelineIds,
        getSelectedTalentPipelineRoleIds,
      }}
    >
      {children}
    </OccupationRolesContext.Provider>
  );
};

export default OccupationRolesProvider;
